/**
 * THE APP (I1): composes every module (contracts/ui.ts) and runs the phases (spec §E.7).
 *
 *   boot        preload.warm(the MapLibre worker + the first view's glyphs, ./warm.ts) and preload.start()
 *               (and the service worker in production), window.__ld in dev / ?qa
 *   loading     <LoaderScreen/> covers everything. MapStage waits for preload.essential, creates the
 *               controller, the bridge builds the choreographer and parks the camera at the intro's
 *               opening view. "Open the map" appears once that frame is drawn (bridge.stageReady).
 *   enter       onEnter → phase "intro", <IntroOverlay/> mounts and runs choreo.intro() while the
 *               loader fades above it (full on a first visit; short on a return visit, under reduced
 *               motion, or when the link names a place or a category).
 *   explore     at the intro's "ui" beat (B6). Deep links apply then (useUrlSync).
 *   drive       card "Take the drive" → actions.startDrive(); bindChoreographer runs choreo.drive().
 *   attract     kiosk only (useKiosk).
 *
 * URL flags: ?kiosk=1 (kiosk shell, auto-enter), ?qa (window.__ld, auto-enter),
 * ?intro=full|short (force the intro), ?light=gl|fallback, ?pr=1..2 (read by MapStage).
 */
import "@/design";
import { useCallback, useEffect, useMemo, useState, type JSX } from "react";
import * as maplibregl from "maplibre-gl";
// ?worker&url: the same bundled worker the controller sets (src/map/controller.ts), so the URL matches.
import workerUrl from "maplibre-gl/dist/maplibre-gl-worker.mjs?worker&url";
import type { MapController } from "@/contracts/map";
import { AppLayout } from "@/features/layout/AppLayout";
import type { AnnounceMoment } from "@/features/a11y/useAnnouncements";
import { useUrlSync } from "@/features/url/useUrlSync";
import { LoaderScreen } from "@/features/loader/LoaderScreen";
import { LOADER_COPY } from "@/features/loader/copy";
import { preload } from "@/features/loader/preload";
import { registerSW } from "@/features/loader/registerSW";
import { IntroOverlay } from "@/features/intro/IntroOverlay";
import { AttractOverlay } from "@/features/kiosk/AttractOverlay";
import { useKiosk } from "@/features/kiosk/useKiosk";
import { PlacesPanel } from "@/features/panel/PlacesPanel";
import { PlaceCard } from "@/features/card/PlaceCard";
import { RouteMeter } from "@/features/card/RouteMeter";
import { DriveHud } from "@/features/card/DriveHud";
import { Hud } from "@/features/hud/Hud";
import { MapStage } from "@/map/MapStage";
import { actions, appStore, getApp, useApp } from "@/store/store";
import { createBridge, stageReady, type Bridge } from "./bridge";
import { debugWanted, installDebug } from "./debug";
import { introModeFor, pageFlags, type IntroMode } from "./flags";
import { warmSet } from "./warm";

/* ── page-level facts, read once ───────────────────────────────────────── */

const SEARCH = typeof location !== "undefined" ? location.search : "";
const FLAGS = pageFlags(SEARCH);
const KIOSK = FLAGS.kiosk;

const planIntro = (): IntroMode => {
  const s = getApp();
  return introModeFor({ introSeen: s.introSeen, reduced: s.reducedMotion, deepLink: FLAGS.deepLink, forced: FLAGS.forcedIntro });
};

/* ── boot, once per page (StrictMode-safe) ─────────────────────────────── */

/**
 * Kiosk scope for the design system on <html> (portalled surfaces included): data-ld-kiosk, and the
 * mood in data-ld-mood, which the 4K glass follows (design/css/system-fixes.css §4, L2-03).
 */
function markKioskRoot(): void {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  root.setAttribute("data-ld-kiosk", "");
  root.setAttribute("data-ld-mood", getApp().mood);
  appStore.subscribe((s, p) => {
    if (s.mood !== p.mood) root.setAttribute("data-ld-mood", s.mood);
  });
}

/** The worker script is in: start MapLibre's worker pool now, while the packs are still arriving (L2-11). */
function prewarmMap(): void {
  try {
    maplibregl.setWorkerUrl(workerUrl);
    maplibregl.prewarm();
  } catch (e) {
    console.warn("[app] map prewarm", e); // the map starts its workers itself
  }
}

let live: { ctrl: MapController | null; bridge: Bridge | null } = { ctrl: null, bridge: null };
let booted = false;
function boot(): void {
  if (booted) return;
  booted = true;
  if (KIOSK) markKioskRoot();
  preload.warm(warmSet(workerUrl, prewarmMap)); // the worker and the first view's glyphs load with the packs
  preload.start();
  registerSW(); // production only
  if (debugWanted(SEARCH)) installDebug({ controller: () => live.ctrl, choreo: () => live.bridge?.choreo ?? null });
}
boot();

/** Announcement moments another module already speaks (B7: group, search; B1: load error; B9: mood on desktop). */
const SKIP_DESKTOP: readonly AnnounceMoment[] = ["group", "search", "loadError", "mood"];
const SKIP_MOBILE: readonly AnnounceMoment[] = ["group", "search", "loadError"];

export function App(): JSX.Element {
  const layout = useApp((s) => s.layout);
  const mobile = layout === "mobile";

  const [ctrl, setCtrl] = useState<MapController | null>(null);
  const [bridge, setBridge] = useState<Bridge | null>(null);
  const [introMode] = useState<IntroMode>(planIntro);
  const [entered, setEntered] = useState(false);
  const [introOver, setIntroOver] = useState(false);

  /* the bridge lives as long as the controller */
  useEffect(() => {
    if (!ctrl) return undefined;
    const b = createBridge(ctrl, { introMode });
    live = { ctrl, bridge: b };
    setBridge(b);
    return () => {
      if (live.bridge === b) live = { ctrl: null, bridge: null };
      b.destroy();
      setBridge((cur) => (cur === b ? null : cur));
    };
  }, [ctrl, introMode]);

  const choreo = bridge?.choreo ?? null;

  useUrlSync();
  useKiosk({ choreo, enabled: KIOSK });

  const onMapError = useCallback((err: unknown) => {
    console.error("[app] the map could not start", err);
    actions.setLoadError(LOADER_COPY.error);
  }, []);

  const onEnter = useCallback(() => {
    setEntered(true);
    if (getApp().phase === "loading") actions.setPhase("intro");
  }, []);

  const onIntroDone = useCallback(() => setIntroOver(true), []);
  const onHome = useCallback(() => live.bridge?.home(), []);
  const onDrive = useCallback(() => actions.startDrive(), []);
  const onClose = useCallback(() => actions.select(null, "card"), []);
  const onStopDrive = useCallback(() => actions.stopDrive(), []);
  const onExtraPins = useCallback((id: string) => live.bridge?.showExtraPins(id), []);

  const map = useMemo(() => <MapStage onReady={setCtrl} onError={onMapError} />, [onMapError]);

  return (
    <>
      <AppLayout
        map={map}
        panel={<PlacesPanel embedded={mobile} onHome={onHome} />}
        card={<PlaceCard onDrive={onDrive} onClose={onClose} onShowExtraPins={onExtraPins} />}
        hud={<Hud controller={ctrl} onRecenter={onHome} />}
        overlays={
          <>
            {entered && !introOver && <IntroOverlay choreo={choreo} mode={introMode} onDone={onIntroDone} />}
            {KIOSK && <AttractOverlay />}
            <RouteMeter />
            <DriveHud onStop={onStopDrive} />
          </>
        }
        announceSkip={mobile ? SKIP_MOBILE : SKIP_DESKTOP}
      />
      <LoaderScreen onEnter={onEnter} auto={KIOSK || FLAGS.qa} until={stageReady} />
    </>
  );
}
